import type { ChatMessage } from '@/types/chat';

/**
 * Dit si l'historique persisté d'une conversation se termine SANS réponse finale de
 * l'agent — le signe qu'un tour est peut-être encore en cours ailleurs (autre instance,
 * page précédente fermée par F5) ou qu'il a été interrompu.
 *
 * Lecture de la forme wire (`adaptHistoryToWire`) : les lignes `tool` ne sont jamais
 * émises ; un message assistant qui porte `metadata.tools_executed` non vide est une
 * ligne INTERMÉDIAIRE (appel d'outils persisté avant exécution) ; la réponse finale est
 * un assistant sans outil. Les marqueurs de compaction (`role: 'system'`) sont ignorés.
 *
 * WHY un indice et pas une preuve : la base ne porte aucun état « tour en cours » (il
 * vit dans le verrou Redis). L'indice décide seulement d'OUVRIR une socket et de
 * demander la reprise ; c'est le backend qui tranche (adoption, `turn_in_progress`,
 * ou `resumed:false` → rechargement). Un faux positif coûte une socket ouverte et un
 * rechargement d'historique, jamais un tour perdu.
 */
export function historyLooksInFlight(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message) continue;
    if (message.role === 'system') continue;
    if (message.role === 'user') return true;
    if (message.role === 'assistant') {
      const toolsExecuted = (message.metadata as { tools_executed?: unknown } | undefined)?.tools_executed;
      return Array.isArray(toolsExecuted) && toolsExecuted.length > 0;
    }
    return false;
  }
  return false;
}

/** Le dernier message user de l'historique : c'est lui que le tour repris projette. */
export function lastUserMessage(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role === 'user') return message;
  }
  return null;
}
